import { useEffect, useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useListeCourses, Coche } from './Courses.tsx'

/**
 * MODE MAGASIN — un mode, pas un écran.
 *
 * Il prend tout l'écran, barre d'onglets comprise, parce qu'on y fait une
 * seule chose : cocher en marchant, d'une main, en poussant un caddie.
 *
 * Trois décisions qui viennent du terrain :
 *
 *  - Les lignes cochées NE REMONTENT PAS. C'est fonctionnellement tentant
 *    (ça raccourcirait la liste), mais la ligne qui bouge déplace la suivante
 *    sous le doigt et provoque un faux tap. La liste raccourcit au niveau du
 *    RAYON, pas de la ligne.
 *  - Pas de texte barré : à 22 px, sur un téléphone qui bouge, un barré est
 *    plus dur à lire qu'une simple atténuation.
 *  - Wake lock. Un écran qui s'éteint toutes les 30 secondes pendant qu'on
 *    coche 27 articles est le pire défaut possible de cet écran, et ce n'est
 *    pas du CSS.
 */
export function ModeMagasin({ onSortir }: { onSortir: () => void }) {
  const { lignes } = useListeCourses()
  const [horsLigne, setHorsLigne] = useState(!navigator.onLine)
  const [bilan, setBilan] = useState(false)

  useEffect(() => {
    let verrou: WakeLockSentinel | null = null
    const prendre = async () => {
      try {
        verrou = (await navigator.wakeLock?.request('screen')) ?? null
      } catch {
        /* Refusé (batterie faible, onglet masqué) : pas grave, on continue. */
      }
    }
    void prendre()
    // Le verrou est relâché par le système quand l'écran passe en arrière-plan :
    // il faut le redemander au retour, sinon il ne tient qu'une fois.
    const reprendre = () => document.visibilityState === 'visible' && void prendre()
    document.addEventListener('visibilitychange', reprendre)

    const on = () => setHorsLigne(false)
    const off = () => setHorsLigne(true)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)

    return () => {
      void verrou?.release()
      document.removeEventListener('visibilitychange', reprendre)
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  const parRayon = useMemo(() => {
    const groupes = new Map<string, typeof lignes>()
    for (const l of lignes) {
      const cle = l.rayon ?? 'Autres'
      groupes.set(cle, [...(groupes.get(cle) ?? []), l])
    }
    return [...groupes.entries()]
  }, [lignes])

  const pris = lignes.filter((l) => l.state === 'pris')
  const total = lignes.length

  function valider() {
    magasin.dispatch('trip.validate', {
      tripId: magasin.id(),
      itemIds: pris.map((l) => l.id),
      /*
       * Un identifiant d'avance par ligne, même si la plupart ne servira
       * pas : un produit acheté pour la première fois n'a aucune ligne
       * d'inventaire, et c'est ici qu'il faut la créer.
       *
       * Les identifiants viennent du CLIENT parce que l'écran est dérivé
       * de `fold(applyOp, serveur, outbox)` : le même gestionnaire tourne
       * des deux côtés, et un tirage au sort côté serveur donnerait deux
       * lignes différentes. En tirer quelques-uns pour rien ne coûte rien.
       */
      nouveauxIds: Object.fromEntries(pris.map((l) => [l.id, magasin.id()])),
    })
    setBilan(true)
  }

  return (
    <div
      className="mode-magasin fixed inset-0 z-40 flex flex-col bg-canvas"
      style={{ animation: 'monter 460ms var(--ease-drawer)' }}
    >
      <header
        className="shrink-0 border-b border-line bg-canvas"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-[var(--size-bar)] items-center justify-between px-4">
          <button type="button" onClick={onSortir} className="pressable text-body text-muted">
            Quitter
          </button>
          <span className="tnum text-meta text-muted">
            {pris.length} / {total}
          </span>
        </div>
        {horsLigne && (
          <div
            className="bg-surface-sunk px-4 py-2 text-center text-meta text-ink-soft"
            style={{ animation: 'monter 240ms var(--ease-out)' }}
          >
            Hors ligne · tes coches sont gardées
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2">
        {parRayon.map(([rayon, items]) => {
          const tousPris = items.every((i) => i.state === 'pris')
          return (
            <section key={rayon} className="mb-4">
              <h2 className="px-1 py-1.5 text-label uppercase text-muted">
                {rayon} {tousPris && <span aria-hidden>·&nbsp;fini</span>}
              </h2>
              {/* La liste raccourcit au niveau du RAYON : il se replie 300 ms
                  après la dernière coche, le temps qu'on la voie atterrir. */}
              <div
                className="rayon-collapse overflow-hidden rounded-md shadow-row"
                style={{
                  maxHeight: tousPris ? 0 : `${items.length * 78}px`,
                  opacity: tousPris ? 0 : 1,
                  transition: 'max-height 260ms var(--ease-in-out) 300ms, opacity 260ms var(--ease-in-out) 300ms',
                }}
              >
                {items.map((l, i) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      magasin.dispatch('list.check', { id: l.id, checked: l.state !== 'pris' })
                      navigator.vibrate?.(10)
                    }}
                    /* pressable-lg : sur 76 px, scale(0.97) ne déplace que
                       2 px et paraît mou. */
                    className={`pressable-lg flex min-h-[76px] w-full items-center gap-4 bg-surface px-4 text-left ${
                      i > 0 ? 'border-t border-line' : ''
                    }`}
                  >
                    <span className={`flex-1 text-store ${l.state === 'pris' ? 'text-muted opacity-45' : 'text-ink'}`}>
                      {l.nom}
                    </span>
                    {/* La quantité se lit au rayon, d'un coup d'œil : elle
                        est ICI plutôt que sous le nom, pour ne pas allonger
                        la ligne — la hauteur du rayon replié en dépend. */}
                    {l.quantityText && (
                      <span className={`tnum shrink-0 text-head ${l.state === 'pris' ? 'text-muted opacity-45' : 'text-ink-soft'}`}>
                        {l.quantityText}
                      </span>
                    )}
                    {/* La coche est À DROITE : c'est là que le pouce droit
                        atteint en marchant. */}
                    <Coche cochee={l.state === 'pris'} grande />
                  </button>
                ))}
              </div>
            </section>
          )
        })}
      </div>

      <footer
        className="shrink-0 border-t border-line bg-canvas px-4 pt-3"
        style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div aria-hidden className="mb-3 h-1 overflow-hidden rounded-full bg-surface-sunk">
          <div
            className="h-full origin-left rounded-full bg-ok"
            style={{
              transform: `scaleX(${total ? pris.length / total : 0})`,
              transition: 'transform 260ms var(--ease-out)',
            }}
          />
        </div>
        <button
          type="button"
          disabled={pris.length === 0}
          onClick={valider}
          className="pressable h-[52px] w-full rounded-sm bg-accent text-head text-accent-ink disabled:opacity-40"
        >
          Valider les courses
        </button>
      </footer>

      {bilan && <Bilan nb={pris.length} horsLigne={horsLigne} onFermer={onSortir} />}
    </div>
  )
}

/**
 * Le bilan de sortie — le seul budget de « delight » de l'application.
 *
 * Il dit la VÉRITÉ sur la synchronisation plutôt qu'une coche verte
 * mensongère : sur iPhone il n'y a pas de Background Sync, donc ranger le
 * téléphone en sortant du magasin ne synchronise rien. Prétendre le contraire
 * serait le genre de petit mensonge qui coûte la confiance au premier
 * désaccord entre deux téléphones.
 */
function Bilan({ nb, horsLigne, onFermer }: { nb: number; horsLigne: boolean; onFermer: () => void }) {
  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-canvas px-8 text-center">
      <p className="font-serif text-display text-ink" style={{ animation: 'pop 220ms var(--ease-out)' }}>
        {nb} article{nb > 1 ? 's' : ''} rangé{nb > 1 ? 's' : ''}
      </p>
      <p className="mt-2 text-body text-muted">L'inventaire est à jour.</p>
      <p className="mt-6 max-w-xs text-meta text-muted">
        {horsLigne
          ? 'Validé sur ce téléphone. Les autres le verront dès que Popote retrouvera du réseau.'
          : 'Tout le monde voit la mise à jour.'}
      </p>
      <button
        type="button"
        onClick={onFermer}
        className="pressable mt-8 h-[52px] rounded-sm bg-accent px-8 text-head text-accent-ink"
      >
        Terminé
      </button>
    </div>
  )
}
